import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Package, X, ShoppingCart, Archive, Clock, Eye, EyeOff, Menu, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import ChatMessage from './ChatMessage';
import { getBackendErrorMessage } from '../../utils/toastError';
import { resolveImageUrl } from '../../utils/urlHelper';

const StoreStaffChatTab = ({ storeId, storeSlug, storeLogo, storeName, embedded = false, onNewIncomingForStaff, initialSelectedConversationId = null }) => {
  const { socket } = useSocket();
  const { user } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [error, setError] = useState(null);

  // /SKU command state
  const [skuResults, setSkuResults] = useState([]);
  const [skuSearchTerm, setSkuSearchTerm] = useState('');
  const [showSkuResults, setShowSkuResults] = useState(false);
  const [showStale, setShowStale] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const messagesEndRef = useRef(null);
  const selectedConversationIdRef = useRef(initialSelectedConversationId || null);
  const lastLoadedConversationIdRef = useRef(null);

  useEffect(() => {
    if (initialSelectedConversationId) {
      selectedConversationIdRef.current = initialSelectedConversationId;
    }
  }, [initialSelectedConversationId]);

  const getDraftKey = useCallback(() => {
    if (!storeId || !user || !selectedConversation) return null;
    return `storeStaffDraft:${storeId}:${user.id}:${selectedConversation.id}`;
  }, [storeId, user, selectedConversation]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const sortConversationsByActivity = useCallback((list) => {
    return [...list].sort((a, b) => {
      const aTime = a?.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bTime = b?.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bTime - aTime;
    });
  }, []);

  const upsertConversation = useCallback((incoming) => {
    if (!incoming?.id) return;

    setConversations((prev) => {
      const existing = prev.find((conv) => String(conv.id) === String(incoming.id));
      const merged = existing ? { ...existing, ...incoming } : incoming;
      const next = existing
        ? prev.map((conv) => (String(conv.id) === String(incoming.id) ? merged : conv))
        : [merged, ...prev];

      return sortConversationsByActivity(next);
    });

    setSelectedConversation((prev) => {
      if (!prev) {
        return incoming;
      }

      if (String(prev.id) !== String(incoming.id)) {
        return prev;
      }

      return { ...prev, ...incoming };
    });
  }, [sortConversationsByActivity]);

  const appendMessageToActiveConversation = useCallback((incomingMessage) => {
    if (!incomingMessage?.id) return;

    setMessages((prev) => {
      if (prev.some((msg) => String(msg.id) === String(incomingMessage.id))) {
        return prev;
      }

      return [...prev, incomingMessage];
    });

    scrollToBottom();
  }, []);

  const loadConversations = useCallback(async () => {
    if (!storeId) return;
    try {
      setLoadingConversations(true);
      const response = await axios.get(`/api/store/messaging/${storeId}/conversations`, {
        params: { type: 'customer', status: 'open' }
      });
      const list = sortConversationsByActivity(response?.data?.conversations || []);
      const preferredId = selectedConversationIdRef.current;

      setConversations(list);
      setSelectedConversation((prev) => {
        const currentId = preferredId || prev?.id;
        if (currentId) {
          const matched = list.find((conv) => String(conv.id) === String(currentId));
          if (matched) return matched;
        }

        return list[0] || null;
      });
    } catch (err) {
      console.error('Error cargando conversaciones de tienda:', err);
      setError('Error al cargar conversaciones de clientes.');
    } finally {
      setLoadingConversations(false);
    }
  }, [storeId, sortConversationsByActivity]);

  const loadMessages = useCallback(
    async (conversationId) => {
      if (!storeId || !conversationId) return;
      try {
        setLoadingMessages(true);
        lastLoadedConversationIdRef.current = conversationId;
        const response = await axios.get(
          `/api/store/messaging/${storeId}/conversations/${conversationId}/messages`,
          { params: { limit: 200 } }
        );
        const rows = response?.data?.messages || [];
        if (String(lastLoadedConversationIdRef.current) === String(conversationId)) {
          setMessages(rows);
        }
        scrollToBottom();
      } catch (err) {
        console.error('Error cargando mensajes de conversación de tienda:', err);
        setError('Error al cargar mensajes de la conversación.');
      } finally {
        setLoadingMessages(false);
      }
    },
    [storeId]
  );

  const lastDraftKeyRef = useRef(null);
  useEffect(() => {
    const key = getDraftKey();
    if (key && key !== lastDraftKeyRef.current) {
      try {
        const saved = sessionStorage.getItem(key);
        setInputMessage(saved || '');
      } catch (e) {
        setInputMessage('');
      }
    } else if (!key && lastDraftKeyRef.current) {
      setInputMessage('');
    }
    lastDraftKeyRef.current = key;
  }, [getDraftKey]);

  useEffect(() => {
    if (!storeId) return;
    loadConversations();
  }, [storeId, loadConversations]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversation?.id || null;
  }, [selectedConversation]);

  useEffect(() => {
    if (!storeId) {
      lastLoadedConversationIdRef.current = null;
    }
  }, [storeId]);

  useEffect(() => {
    if (!socket || !storeId) return;

    socket.emit('store:join_staff_room', { storeId });

    return () => {
      socket.emit('store:leave_staff_room', { storeId });
    };
  }, [socket, storeId]);

  useEffect(() => {
    if (!socket || !storeId || !selectedConversation) return;

    const conversationId = selectedConversation.id;
    socket.emit('store:join_conversation', { storeId, conversationId });

    return () => {
      socket.emit('store:leave_conversation', { storeId, conversationId });
    };
  }, [socket, storeId, selectedConversation]);

  useEffect(() => {
    if (!socket || !storeId) return;

    const handleCustomerMessage = (payload) => {
      if (!payload || String(payload.store_id) !== String(storeId)) return;

      if (typeof onNewIncomingForStaff === 'function') {
        onNewIncomingForStaff(payload);
      }

      if (payload.conversation) {
        upsertConversation(payload.conversation);
      }

      if (
        payload.message &&
        selectedConversationIdRef.current &&
        String(payload.conversation_id) === String(selectedConversationIdRef.current)
      ) {
        appendMessageToActiveConversation(payload.message);
      }
    };

    const handleStaffMessage = (payload) => {
      if (!payload || String(payload.store_id) !== String(storeId)) return;

      if (payload.conversation) {
        upsertConversation(payload.conversation);
      }

      if (
        selectedConversationIdRef.current &&
        String(payload.conversation_id) === String(selectedConversationIdRef.current)
      ) {
        appendMessageToActiveConversation(payload);
      }
    };

    const handleAccepted = (payload) => {
      if (!payload || String(payload.store_id) !== String(storeId)) return;

      if (payload.conversation) {
        upsertConversation(payload.conversation);
      }
    };

    const handleStatusUpdated = (payload) => {
      if (!payload || String(payload.store_id) !== String(storeId)) return;
      
      if (payload.status === 'closed' || payload.status === 'archived') {
        // Remover de la lista de abiertas si se cerró
        setConversations(prev => prev.filter(c => String(c.id) !== String(payload.conversation_id)));
        if (selectedConversationIdRef.current && String(payload.conversation_id) === String(selectedConversationIdRef.current)) {
          setSelectedConversation(null);
          setMessages([]);
        }
      } else if (payload.conversation) {
        upsertConversation(payload.conversation);
      }
    };

    socket.on('store:conversation:customer-message', handleCustomerMessage);
    socket.on('store:conversation:staff-message', handleStaffMessage);
    socket.on('store:conversation:accepted', handleAccepted);
    socket.on('store:conversation:status-updated', handleStatusUpdated);

    return () => {
      socket.off('store:conversation:customer-message', handleCustomerMessage);
      socket.off('store:conversation:staff-message', handleStaffMessage);
      socket.off('store:conversation:accepted', handleAccepted);
      socket.off('store:conversation:status-updated', handleStatusUpdated);
    };
  }, [socket, storeId, upsertConversation, appendMessageToActiveConversation, onNewIncomingForStaff]);

  useEffect(() => {
    const selectedConversationId = selectedConversation?.id || null;

    if (!selectedConversationId) {
      lastLoadedConversationIdRef.current = null;
      setMessages([]);
      return;
    }

    if (String(lastLoadedConversationIdRef.current) === String(selectedConversationId) && messages.length > 0) {
      return;
    }

    setMessages([]);
    loadMessages(selectedConversationId);
  }, [selectedConversation?.id, loadMessages]);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputMessage(value);

    const key = getDraftKey();
    if (!key) return;
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      // ignore storage errors
    }
  };

  const handleAcceptConversation = async (conv) => {
    if (!storeId || !conv || !user) return;
    setAcceptingId(conv.id);
    setError(null);
    try {
      const response = await axios.post(
        `/api/store/messaging/${storeId}/conversations/${conv.id}/accept`
      );
      const updated = response?.data?.conversation || null;
      if (updated) {
        upsertConversation(updated);
        setSelectedConversation(updated);
        await loadMessages(updated.id);
      } else {
        await loadConversations();
      }
    } catch (err) {
      setError(getBackendErrorMessage(err, 'Error al aceptar conversación.'));
    } finally {
      setAcceptingId(null);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !user || !storeId || !selectedConversation || sending) {
      return;
    }

    const text = inputMessage.trim();
    setSending(true);
    setError(null);
    setShowSkuResults(false);

    try {
      const response = await axios.post(
        `/api/store/messaging/${storeId}/conversations/${selectedConversation.id}/messages`,
        { message: text }
      );
      
      // Check if this was a /SKU command response
      if (response?.data?.command === 'sku') {
        setSkuResults(response.data.results || []);
        setSkuSearchTerm(response.data.searchTerm || '');
        setShowSkuResults(true);
        const key = getDraftKey();
        if (key) {
          try {
            sessionStorage.removeItem(key);
          } catch (e) {}
        }
        setInputMessage('');
        if (response.data.error) {
          setError(response.data.error);
        }
        return;
      }
      
      const msg = response?.data?.message || null;
      const updatedConversation = response?.data?.conversation || null;
      if (updatedConversation) {
        upsertConversation(updatedConversation);
      }
      if (msg) {
        appendMessageToActiveConversation(msg);
      } else {
        await loadMessages(selectedConversation.id);
      }
      const key = getDraftKey();
      if (key) {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {}
      }
      setInputMessage('');
    } catch (err) {
      setError(getBackendErrorMessage(err, 'Ocurrió un error al enviar el mensaje.'));
    } finally {
      setSending(false);
    }
  };

  // Send a product card to the conversation
  const handleSendProductCard = async (product) => {
    if (!storeId || !selectedConversation || sending) return;
    
    setSending(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `/api/store/messaging/${storeId}/conversations/${selectedConversation.id}/messages`,
        { 
          message: '', 
          type: 'product_card',
          product: {
            id: product.id,
            name: product.name,
            description: product.description,
            sku: product.sku,
            price: product.price,
            image_url: product.image_url,
            category_name: product.category_name
          }
        }
      );
      
      const msg = response?.data?.message || null;
      const updatedConversation = response?.data?.conversation || null;
      if (updatedConversation) {
        upsertConversation(updatedConversation);
      }
      if (msg) {
        appendMessageToActiveConversation(msg);
      }
      
      // Clear SKU results after sending
      setShowSkuResults(false);
      setSkuResults([]);
      setSkuSearchTerm('');
    } catch (err) {
      setError(getBackendErrorMessage(err, 'Error al enviar producto.'));
    } finally {
      setSending(false);
    }
  };

  const handleArchiveConversation = async (convId) => {
    if (!storeId || !convId || !user) return;
    if (!window.confirm('¿Estás seguro de que deseas cerrar esta conversación? El cliente ya no la verá como activa.')) return;

    setArchivingId(convId);
    try {
      await axios.post(`/api/store/messaging/${storeId}/conversations/${convId}/status`, {
        status: 'closed'
      });
      // El socket handleStatusUpdated se encargará de limpiar el estado
    } catch (err) {
      setError(getBackendErrorMessage(err, 'Error al cerrar conversación.'));
    } finally {
      setArchivingId(null);
    }
  };

  const closeSkuResults = () => {
    setShowSkuResults(false);
    setSkuResults([]);
    setSkuSearchTerm('');
  };

  const STALE_THRESHOLD_HOURS = 48;
  const filteredConversations = conversations.filter(conv => {
    if (showStale) return true;
    if (!conv.last_message_at) return true;
    const lastAt = new Date(conv.last_message_at).getTime();
    const now = Date.now();
    const hoursSinceLast = (now - lastAt) / (1000 * 60 * 60);
    return hoursSinceLast < STALE_THRESHOLD_HOURS;
  });

  const staleCount = conversations.length - filteredConversations.length;

  const renderConversationItem = (conv) => {
    const isSelected = selectedConversation && selectedConversation.id === conv.id;
    const metadata = conv.metadata && typeof conv.metadata === 'object' ? conv.metadata : {};
    const assignedName = metadata.assigned_staff_name || null;

    return (
      <button
        key={conv.id}
        type="button"
        onClick={() => handleSelectConversation(conv)}
        className={`w-full text-left px-2 py-1.5 text-[11px] border-b border-glass/40 hover:bg-glass/40 ${
          isSelected ? 'bg-glass/60' : ''
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-text/90 truncate">
              {conv.customer_name || conv.label || 'Cliente sin nombre'}
            </div>
            <div className="text-[10px] text-text/80 truncate">
              {conv.last_message_preview || 'Sin mensajes recientes'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {assignedName && (
              <div className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold truncate max-w-[140px]">
                Staff
              </div>
            )}
            {!assignedName && (
              <button
                type="button"
                className="ml-2 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700 disabled:bg-emerald-300 disabled:text-white disabled:opacity-70"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAcceptConversation(conv);
                }}
                disabled={!!acceptingId && acceptingId !== conv.id}
              >
                {acceptingId === conv.id ? 'Aceptando…' : 'Aceptar'}
              </button>
            )}
          </div>
        </div>
      </button>
    );
  };
  const innerContent = (
    <div className="flex h-full store-staff-panel relative bg-white">
      {/* Sidebar de Conversaciones */}
      <div 
        className={`border-r border-glass flex flex-col bg-white transition-all duration-300 ease-in-out z-10 ${
          sidebarOpen ? 'w-[180px] opacity-100' : 'w-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="p-2 border-b border-glass flex flex-col gap-1 bg-gray-50 min-h-[50px] justify-center">
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-extrabold uppercase tracking-tighter text-gray-900">Chats</span>
            {staleCount > 0 && (
              <button
                onClick={() => setShowStale(!showStale)}
                className={`flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-bold transition-all ${
                  showStale 
                    ? 'bg-amber-100 text-amber-700' 
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                title={showStale ? 'Ocultar antiguos' : 'Ver antiguos'}
              >
                {showStale ? <EyeOff size={8} /> : <Eye size={8} />}
                {showStale ? 'Ocultar' : `${staleCount} ant.`}
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-white">
          {loadingConversations ? (
            <div className="p-3 text-[11px] text-gray-500">Cargando conversaciones…</div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-3 text-[11px] text-gray-500">
              {showStale 
                ? 'No hay conversaciones abiertas.' 
                : 'No hay conversaciones activas.'}
            </div>
          ) : (
            filteredConversations.map((conv) => renderConversationItem(conv))
          )}
        </div>
      </div>

      {/* Botón Flotante para abrir/cerrar sidebar si está cerrado */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="absolute left-0 top-2 z-20 bg-white border border-glass shadow-md p-1.5 rounded-r-md text-gray-600 hover:text-gray-900"
        >
          <ChevronRight size={18} />
        </button>
      )}

      <div className="flex-1 flex flex-col">
        <div className="px-2 py-1.5 border-b border-glass flex items-center gap-1.5 bg-white sticky top-0 z-10 shadow-sm min-w-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-all flex-shrink-0"
            title={sidebarOpen ? 'Cerrar lateral' : 'Abrir lateral'}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <Menu size={16} />}
          </button>
          
          <select
            className="flex-1 bg-white border border-gray-300 rounded px-1.5 py-1 text-[10px] text-gray-900 focus:ring-1 focus:ring-blue-500 outline-none min-w-0"
            value={selectedConversation ? selectedConversation.id : ''}
            onChange={(e) => {
              const conv = conversations.find((c) => String(c.id) === String(e.target.value));
              if (conv) {
                handleSelectConversation(conv);
              }
            }}
          >
            <option value="" className="bg-white text-gray-900">Cliente…</option>
            {conversations.map((conv) => (
              <option key={conv.id} value={conv.id} className="bg-white text-gray-900">
                {conv.customer_name || conv.label || 'Cliente'}
              </option>
            ))}
          </select>
          {selectedConversation && (
            <button
              onClick={() => handleArchiveConversation(selectedConversation.id)}
              disabled={!!archivingId}
              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all flex-shrink-0"
              title="Archivar"
            >
              <Archive size={14} />
            </button>
          )}
        </div>

        <div className="messages-area">
          {!selectedConversation ? (
            <div className="empty-messages">
              <p>Selecciona una conversación de la lista</p>
              <p className="text-sm">
                Aquí verás los mensajes del cliente y podrás responder como staff.
              </p>
            </div>
          ) : loadingMessages ? (
            <div className="empty-messages">
              <p>Cargando mensajes…</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="empty-messages">
              <p>Aún no hay mensajes en esta conversación</p>
            </div>
          ) : (
            <>
              {messages.map((msg) => {
                const isCustomer = msg.author_type === 'customer';
                const username = isCustomer
                  ? msg.customer_name || msg.customer_username || 'Cliente'
                  : msg.staff_name || msg.staff_username || 'Staff';
                const isOwn = !isCustomer && msg.author_user_id === user?.id;

                return (
                  <ChatMessage
                    key={msg.id}
                    username={username}
                    message={msg.message}
                    timestamp={msg.created_at}
                    isOwn={isOwn}
                    showUsername={true}
                    metadata={msg.metadata}
                    storeSlug={storeSlug}
                    storeLogo={storeLogo}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {showSkuResults && (
          <div className="bg-glass/80 border-t border-white/10 p-3 max-h-48 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-text/80">
                <Package size={14} className="inline mr-1" />
                Resultados para "{skuSearchTerm}" ({skuResults.length})
              </div>
              <button
                type="button"
                onClick={closeSkuResults}
                className="p-1 hover:bg-glass/60 rounded"
              >
                <X size={14} className="text-text/60" />
              </button>
            </div>

            {skuResults.length === 0 ? (
              <div className="text-[11px] text-text/50 text-center py-2">
                No se encontraron productos con ese término
              </div>
            ) : (
              <div className="space-y-1">
                {skuResults.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-2 p-2 bg-glass/40 rounded hover:bg-glass/60 cursor-pointer group"
                    onClick={() => handleSendProductCard(product)}
                  >
                    {product.image_url ? (
                      <img
                        src={resolveImageUrl(product.image_url)}
                        alt={product.name}
                        className="w-8 h-8 object-cover rounded"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-glass/40 rounded flex items-center justify-center">
                        <Package size={14} className="text-text/40" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold text-text truncate">{product.name}</div>
                      <div className="text-[10px] text-text/50">
                        {product.sku && <span>SKU: {product.sku} · </span>}
                        {product.category_name || 'Sin categoría'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] font-bold text-accent">
                        ${Number(product.price || 0).toFixed(2)}
                      </div>
                      <button
                        type="button"
                        className="text-[9px] text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                      >
                        <Send size={10} /> Enviar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <form className="message-input" onSubmit={handleSendMessage}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={
              selectedConversation
                ? 'Escribe /SKU término para buscar productos…'
                : 'Selecciona una conversación para responder…'
            }
            maxLength={1000}
            disabled={sending || !selectedConversation}
          />
          <button
            type="submit"
            disabled={
              sending || !inputMessage.trim() || !socket || !user || !selectedConversation
            }
          >
            <Send size={18} />
          </button>
        </form>

        {error && (
          <div className="px-3 pb-2 text-xs text-red-500">
            {error}
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) {
    return innerContent;
  }

  return (
    <div className="tab-panel">
      <div className="tab-info">
        <span className="tab-title">🏬 Chat de tienda (Staff)</span>
        <span className="tab-subtitle">
          Atiende conversaciones con clientes de {storeName || 'la tienda'}
        </span>
      </div>

      {innerContent}
    </div>
  );
}

export default StoreStaffChatTab;
